// src/pages/StockAnalysis.jsx
import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Input,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Text,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import axios from 'axios';
import StockData from '../../components/analysis/stockData';
import DCFAnalysis from '../../components/analysis/DCF';
import DDMAnalysis from '../../components/analysis/DDM';
import formatCurrency from '../../utility/formatCurrency';

const StockAnalysis = () => {
  // Stati di base
  const [ticker, setTicker] = useState('');
  const [stockData, setStockData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [balancesheet, setBalancesheet] = useState(null);

  const [isStockDataLoaded, setIsStockDataLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [analysisName, setAnalysisName] = useState('');
  const [notes, setNotes] = useState('');
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);



  // Stato per le analisi: per ogni modello vengono salvati i parametri, il forecast (array) e il valore finale
  const defaultAnalysisModels =  {
    DCF: {
      parameters: {
        growthRate: '',
        discountRate: '',
        terminalGrowthRate: '',
        forecastYears: '', // Numero di anni da forecastare
      },
      forecast: [],
      terminalValue: null,
      enterpriseValue: null,
      equityValue: null,
      stockValue: null,
    },
    DDM: {
      parameters: {
        dividendGrowthRate: '',
        requiredReturnRate: '',
        forecastYears: '',
        modelType: 'Gordon',
      },
      forecast: [],
      stockValue: null,
    },
  };
  const [analysisModels, setAnalysisModels] = useState(defaultAnalysisModels);

  // Recupera i dati finanziari dal backend
  const fetchFinancialData = async () => {
    setAnalysisModels(defaultAnalysisModels);
    setLoading(true);
    setError(null);
    setStockData(null);
    setCashFlowData(null);
    setBalancesheet(null);
    setPriceData(null);
    setInfoMsg(null);
    setIsStockDataLoaded(false);
    try {
      // Retrieve basic infos on the company
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/market_data/${ticker}/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStockData(response.data);
      console.log(response.data);

      // Retrieve cash flow data
      const cashFlowResponse = await axios.get(`/api/market_data/${ticker}/cashflow`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // setCashFlowData(JSON.parse(cashFlowResponse.data));
      setCashFlowData(cashFlowResponse.data);

      // Retrieve price data
      const priceResponse = await axios.get(`/api/market_data/${ticker}/price_history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const priceData = priceResponse.data.map((sample) => {})
      setPriceData(priceResponse.data);

      // Retrieve the balance sheet
      const balancesheetResponse = await axios.get(`/api/market_data/${ticker}/balancesheet`, {
          headers: { Authorization: `Bearer ${token}` },
      });
      setBalancesheet(balancesheetResponse.data);
      
      // Recupera le analisi salvate
      const analysesResponse = await axios.get(`/api/analysis/${ticker}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedAnalyses(analysesResponse.data);
    } catch (err) {
      console.error(err);
      setError('Errore nel recupero dei dati. Verifica il ticker e riprova.');
      setIsStockDataLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stockData != null && cashFlowData != null && balancesheet != null && priceData != null) {
      setIsStockDataLoaded(true);
    }
  }, [stockData, cashFlowData, balancesheet, priceData]);

  const handleAnalysisSelect = async (analysisId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/analysis/${analysisId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedAnalysis(response.data);
      setAnalysisModels(response.data.models);
      setAnalysisName(response.data.title);
      setNotes(response.data.notes);
    } catch (err) {
      console.error(err);
      setError('Errore nel caricamento dell\'analisi.');
    }
  };

  const deleteAnalysis = async (analysisId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/analysis/${analysisId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const analysesResponse = await axios.get(`/api/analysis/${ticker}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedAnalyses(analysesResponse.data);
      setInfoMsg('Analisi eliminata con successo!');
    } catch (err) {
      console.error(err);
      setError('Errore nell\'eliminazione dell\'analisi.');
    }
  };

  const updateAnalysis = async () => {
    if (!selectedAnalysis) {
      setError('Seleziona un\'analisi da aggiornare.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ticker,
        stockData,
        analysisName,
        notes,
        models: analysisModels,
      };
      await axios.put(`/api/analysis/${selectedAnalysis.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update the saved analyses list
      const analysesResponse = await axios.get(`/api/analysis/${ticker}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedAnalyses(analysesResponse.data);
      setInfoMsg('Analisi aggiornata con successo!');
    } catch (err) {
      console.error(err);
      setError('Errore nell\'aggiornamento dell\'analisi.');
    }
  };

  // Gestione della modifica dei parametri per il modello specificato
  const handleParameterChange = (model, event) => {
    const { name, value } = event.target;
    setAnalysisModels((prev) => ({
      ...prev,
      [model]: {
        ...prev[model],
        parameters: {
          ...prev[model].parameters,
          [name]: value,
        },
      },
    }));
  };

  const handleModelTypeChange = (model, value) => {
    setAnalysisModels((prev) => ({
      ...prev,
      [model]: {
        ...prev[model],
        parameters: {
          ...prev[model].parameters,
          modelType: value,
        },
      },
    }));
  };

  // Funzione per eseguire il calcolo e generare il forecast per un modello
  const calculateModel = (model) => {
    if (!stockData) {
      setError('Prima recupera i dati finanziari.');
      return;
    }
    let stockValue = 0;
    let forecast = [];
    try {
      if (model === 'DCF') {
        // Recupera i parametri
        // const freeCashflow = parseFloat(stockData.freeCashflow);
        const freeCashflow = cashFlowData[cashFlowData.length - 1].freeCashFlow;
        const growthRate = parseFloat(analysisModels.DCF.parameters.growthRate) / 100;
        const discountRate = parseFloat(analysisModels.DCF.parameters.discountRate) / 100;
        const forecastYears = parseInt(analysisModels.DCF.parameters.forecastYears, 10);
        const terminalRate = parseFloat(analysisModels.DCF.parameters.terminalGrowthRate) / 100;
        if(isNaN(freeCashflow)) throw new Error('Il Free Cash Flow dell\'azienda non è disponibile. Non è pertanto possibile fare l\'analisi DCF.');
        if (
          isNaN(growthRate) ||
          isNaN(discountRate) ||
          isNaN(forecastYears)
        ) {
          throw new Error('Inserisci tutti i parametri validi per DCF.');
        }
        if (discountRate === 0) throw new Error('Il tasso di sconto non può essere zero.');
        // Genera il forecast anno per anno
        var enterpriseValue = 0;

        var freeCashflowForecast = freeCashflow;
        for(let year = 1; year <= forecastYears; year++){
            freeCashflowForecast = freeCashflowForecast * (1 + growthRate); // FCF Forecast for i-th year
            var discountedFreeCashflow = freeCashflowForecast / Math.pow(1 + discountRate, year); // Discounted FCF for i-th year

            forecast.push({
                year,
                forecastValue: freeCashflowForecast.toFixed(2),
                discountedValue: discountedFreeCashflow.toFixed(2),
            });

            enterpriseValue += discountedFreeCashflow;
        }
        // Compute the terminal value
        // var terminalValue = freeCashflowForecast * (1 + growthRate) / (discountRate - growthRate);
        if (isNaN(terminalRate)) {
          var terminalValue = freeCashflowForecast * (1 + growthRate) / (discountRate - growthRate);
        } else {
          var terminalValue = freeCashflowForecast * (1 + terminalRate) / (discountRate - terminalRate);
        }


        // Compute the enterprise value
        enterpriseValue += terminalValue / Math.pow(1 + discountRate, forecastYears);

        // Calcola il valore dell'azione
        var equityValue = enterpriseValue - stockData.totalDebt;

        stockValue = equityValue / stockData.sharesOutstanding;

        setAnalysisModels((prev) => ({
            ...prev,
            [model]: {
              ...prev[model],
              stockValue: stockValue,
              forecast,
              terminalValue: terminalValue,
              enterpriseValue: enterpriseValue,
              equityValue: equityValue,
            },
        }));

      } else if (model === 'DDM') {
        // Recupera i parametri
        const dividend = parseFloat(stockData.dividendRate);
        const dividendGrowthRate = parseFloat(analysisModels.DDM.parameters.dividendGrowthRate) / 100;
        const requiredReturnRate = parseFloat(analysisModels.DDM.parameters.requiredReturnRate) / 100;
        const forecastYears = parseInt(analysisModels.DDM.parameters.forecastYears, 10);
        if (
          isNaN(dividend) ||
          isNaN(dividendGrowthRate) ||
          isNaN(requiredReturnRate) ||
          (analysisModels.DDM.parameters.modelType === 'Multiple' && isNaN(forecastYears))
        ) {
          throw new Error('Inserisci tutti i parametri validi per DDM.');
        }
        if (analysisModels.DDM.parameters.modelType === 'Gordon') {
          // Calcolo con l'equazione di Gordon
          stockValue = dividend * (1 + dividendGrowthRate) / (requiredReturnRate - dividendGrowthRate);
        } else {
          // Genera il forecast anno per anno
          for (let year = 1; year <= forecastYears; year++) {
            const forecastDividend = dividend * Math.pow(1 + dividendGrowthRate, year);
            const discountedDividend = forecastDividend / Math.pow(1 + requiredReturnRate, year);
            forecast.push({
              year,
              forecastDividend: forecastDividend,
              discountedDividend: discountedDividend,
            });
            stockValue += discountedDividend;
          }
        }
        setAnalysisModels((prev) => ({
          ...prev,
          [model]: {
            ...prev[model],
            stockValue: stockValue,
            forecast,
          },
        }));
      }
      setError(null); // Clear any previous errors
    } catch (error) {
      setError(error.message);
    } 
  };

  // Salva l'intera analisi (con entrambi i modelli) nel backend
  const saveAnalysis = async () => {
    setLoading(true);
    setError(null);
    setInfoMsg(null);
    if (!stockData) {
      setError('Prima recupera i dati finanziari.');
      setLoading(false);
      return;
    }
    if (analysisName === '') {
      setError('Inserisci un nome per l\'analisi.');
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ticker,
        stockData,
        analysisName,
        notes,
        models: analysisModels,
      };
      const response = await axios.post('/api/analysis', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update the saved analyses list
      const analysesResponse = await axios.get(`/api/analysis/${ticker}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedAnalyses(analysesResponse.data);
      setInfoMsg('Analisi salvata con successo!');
      console.log(response.data);
    } catch (err) {
      console.error(err);
      setError('Errore nel salvataggio dell\'analisi.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchFinancialData();
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box p={4} borderWidth={1} borderRadius="lg">
        <form onSubmit={handleSubmit}>
        <FormControl id="ticker" mb={4}>
            <InputGroup>
              <Input
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="Ticker (Es. AAPL, MSFT...)"
              />
              <InputRightElement>
                <IconButton
                  aria-label="Search database"
                  icon={<SearchIcon />}
                  onClick={fetchFinancialData}
                  isLoading={loading}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </form>

        {error && (
          <Alert status="error" mt={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {isStockDataLoaded && (
          <Box mt={6}>
            <StockData stockInfo={stockData} balancesheet={balancesheet} cashflowStatement={cashFlowData} priceHistory={priceData}/>
            <Tabs mt={4} variant="enclosed">
              <TabList>
                <Tab>DCF</Tab>
                <Tab>DDM</Tab>
              </TabList>
              <TabPanels>
                {/* Tab per il modello DCF */}
                <TabPanel>
                  <DCFAnalysis
                    analysisModels={analysisModels}
                    handleParameterChange={handleParameterChange}
                    calculateModel={calculateModel}
                    loading={loading}
                    stockData={stockData}
                    formatCurrency={formatCurrency}
                  />
                </TabPanel>
                {/* Tab per il modello DDM */}
                <TabPanel>
                  <DDMAnalysis
                      analysisModels={analysisModels}
                      handleParameterChange={handleParameterChange}
                      calculateModel={calculateModel}
                      loading={loading}
                      stockData={stockData}
                      formatCurrency={formatCurrency}
                      handleModelTypeChange={handleModelTypeChange}
                    />
                </TabPanel>
              </TabPanels>
            </Tabs>
            {/* Campi per il salvataggio dell'analisi */}
            <FormControl id="analysisName" mt={6} isRequired>
              <FormLabel>Nome dell'analisi</FormLabel>
              <Input
                value={analysisName}
                onChange={(e) => setAnalysisName(e.target.value)}
                placeholder="Es. Analisi di AAPL"/>
            </FormControl>
            <FormControl id="notes" mt={4}>
              <FormLabel>Note</FormLabel>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Es. Note aggiuntive sull'analisi"/>
            </FormControl>
            <Button
              mt={6}
              colorScheme="teal"
              onClick={saveAnalysis}
              isLoading={loading}
            >
              Salva Analisi Completa
            </Button>
            <Divider mt={6} />
            <Text mt={6} fontSize="lg" fontWeight="bold">
              Analisi Salvate
            </Text>

            <Flex alignItems="center" mt={6}>
              
              <FormControl id="savedAnalyses" mr={4} flex="1">
                <Select
                  placeholder="Seleziona un'analisi"
                  onChange={(e) => setSelectedAnalysisId(e.target.value)}
                  minWidth="200px"
                >
                  {savedAnalyses.map((analysis) => (
                    <option key={analysis.id} value={analysis.id}>
                      {analysis.name} - {new Date(analysis.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <Button
                leftIcon={<RepeatIcon />}
                colorScheme="blue"
                onClick={() => handleAnalysisSelect(selectedAnalysisId)}
                isDisabled={!selectedAnalysisId}
                mr={2}
              >
                Ripristina
              </Button>
              <Button
                leftIcon={<EditIcon />}
                colorScheme="yellow"
                onClick={updateAnalysis}
                isDisabled={!selectedAnalysisId}
                mr={2}
              >
                Aggiorna
              </Button>
              <Button
                leftIcon={<DeleteIcon />}
                colorScheme="red"
                onClick={() => deleteAnalysis(selectedAnalysisId)}
                isDisabled={!selectedAnalysisId}
              >
                Elimina
              </Button>
            </Flex>
            {infoMsg && (
              <Alert status="success" mt={4}>
                <AlertIcon />
                {infoMsg}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default StockAnalysis;
